// Merkliste der PIM-Angebote (Development/offer-favorites.md): Schlüssel "uid" bzw.
// "uid|JJJJ-MM-TT" im localStorage dieses Browsers, ohne Login und ohne Server-Speicherung.
// - Herz-Buttons [data-favorite-toggle] (Partials/Offer/FavoriteButton.html): Zustand über aria-pressed
// - Header: Zähler [data-favorites-badge], Ansage für Screenreader [data-favorites-live]
// - Seite "Ihre Favoriten" [data-offer-favorites] (Templates/Offer/Favorites.html): lädt die Karten
//   per POST aus dem Seitentyp bfwOfferFavorites und übernimmt dessen Korrekturen
//   (Termin vorbei -> Angebot ohne Termin, Angebot nicht mehr sichtbar -> entfernt)
// Die Seiten liegen im Cache, der Zustand wird daher erst hier gesetzt; Änderungen aus
// anderen Tabs kommen über das storage-Event.
const STORAGE_KEY = 'bfw-offer-favorites';
const KEY_PATTERN = /^[1-9]\d{0,9}(\|\d{4}-\d{2}-\d{2})?$/;
const BUMP_CLASS = 'favorites-badge--bump';

// Ersatz, falls der Browser localStorage sperrt: Merkliste gilt dann bis zum Neuladen
let fallbackKeys = [];

function readKeys() {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(value)
      ? [...new Set(value.filter((key) => typeof key === 'string' && KEY_PATTERN.test(key)))]
      : [];
  } catch {
    return [...fallbackKeys];
  }
}

function writeKeys(keys) {
  fallbackKeys = [...keys];
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // gesperrt oder voll: fallbackKeys
  }
}

function render(keys, { bump = false } = {}) {
  const active = new Set(keys);
  document.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
    button.setAttribute('aria-pressed', active.has(button.dataset.favoriteToggle) ? 'true' : 'false');
  });

  document.querySelectorAll('[data-favorites-badge]').forEach((badge) => {
    badge.textContent = String(keys.length);
    badge.classList.toggle('hidden', keys.length === 0);
    if (bump && keys.length > 0) {
      badge.classList.remove(BUMP_CLASS);
      void badge.offsetWidth; // Animation neu starten
      badge.classList.add(BUMP_CLASS);
    }
  });

  document.querySelectorAll('[data-favorites-link]').forEach((link) => {
    link.setAttribute('aria-label', keys.length > 0 ? `Ihre Favoriten (${keys.length})` : 'Ihre Favoriten');
  });
}

function announce(text) {
  const live = document.querySelector('[data-favorites-live]');
  if (!live) return;
  // Leeren und neu setzen, damit dieselbe Meldung erneut vorgelesen wird
  live.textContent = '';
  window.setTimeout(() => {
    live.textContent = text;
  }, 50);
}

function removeKey(key) {
  const keys = readKeys().filter((item) => item !== key);
  writeKeys(keys);
  render(keys, { bump: true });
  announce('Von der Merkliste entfernt');
}

export function initOfferFavorites() {
  render(readKeys());

  const page = document.querySelector('[data-offer-favorites]');
  const favoritesPage = page ? initFavoritesPage(page) : null;

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-favorite-toggle]');
    if (!toggle) return;

    const key = toggle.dataset.favoriteToggle;
    const keys = readKeys();
    if (keys.includes(key)) {
      removeKey(key);
      return;
    }
    keys.push(key);
    writeKeys(keys);
    render(keys, { bump: true });
    announce('Zur Merkliste hinzugefügt');
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    render(readKeys());
    favoritesPage?.load();
  });
}

function initFavoritesPage(page) {
  const list = page.querySelector('[data-favorites-list]');
  const summary = page.querySelector('[data-favorites-summary]');
  const notice = page.querySelector('[data-favorites-notice]');
  const clearWrap = page.querySelector('[data-favorites-clear-wrap]');
  const empty = page.querySelector('[data-favorites-empty]');
  const endpoint = page.dataset.offerFavorites;
  if (!list || !summary || !endpoint) return null;

  let controller = null;

  const pluralText = (element, count) => {
    const text = count === 1 ? element.dataset.textOne : element.dataset.textMany;
    return (text ?? '').replace('%d', String(count));
  };

  const updateState = () => {
    const count = list.querySelectorAll('[data-favorite-item]').length;
    summary.textContent = count === 0 ? summary.dataset.textNone : pluralText(summary, count);
    clearWrap?.classList.toggle('hidden', count === 0);
    empty?.classList.toggle('hidden', count > 0);
  };

  // Korrekturen des Servers: alter Schlüssel => neuer Schlüssel, null = entfernen
  const applyChanges = (changes) => {
    const keys = readKeys();
    const updated = [...new Set(
      keys
        .map((key) => (Object.hasOwn(changes, key) ? changes[key] : key))
        .filter((key) => typeof key === 'string'),
    )];
    if (updated.join(',') !== keys.join(',')) {
      writeKeys(updated);
    }
  };

  const load = async () => {
    const keys = readKeys();
    controller?.abort();

    if (keys.length === 0) {
      list.replaceChildren();
      updateState();
      return;
    }

    controller = new AbortController();
    const body = new FormData();
    keys.forEach((key) => body.append('favorites[]', key));
    page.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(endpoint, { method: 'POST', body, signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const result = template.content.querySelector('[data-favorites-result]');
      if (!result) throw new Error('Antwort ohne [data-favorites-result]');

      applyChanges(JSON.parse(result.dataset.favoritesChanges || '{}'));
      list.replaceChildren(result);

      const removed = Number.parseInt(result.dataset.favoritesRemoved ?? '0', 10);
      if (notice) {
        notice.textContent = removed > 0 ? pluralText(notice, removed) : '';
        notice.classList.toggle('hidden', removed === 0);
      }
      updateState();
      render(readKeys());
    } catch (error) {
      if (error.name === 'AbortError') return;
      summary.textContent = 'Ihre Favoritenliste konnte nicht geladen werden. Bitte laden Sie die Seite neu.';
    } finally {
      page.removeAttribute('aria-busy');
    }
  };

  list.addEventListener('click', (event) => {
    const button = event.target.closest('[data-favorite-remove]');
    if (!button) return;

    const item = button.closest('[data-favorite-item]');
    const next = item?.nextElementSibling ?? item?.previousElementSibling;
    removeKey(button.dataset.favoriteRemove);
    item?.remove();
    // Letzte Karte: auch den grauen Rahmen der Liste entfernen
    if (!list.querySelector('[data-favorite-item]')) list.replaceChildren();
    updateState();

    // Fokus nicht verlieren: nächster Eintrag, sonst Link zur Angebotsliste
    const focusTarget = next?.querySelector('[data-favorite-remove]') ?? empty?.querySelector('a');
    focusTarget?.focus();
  });

  page.querySelector('[data-favorites-clear]')?.addEventListener('click', () => {
    writeKeys([]);
    render([], { bump: true });
    list.replaceChildren();
    notice?.classList.add('hidden');
    updateState();
    announce('Favoritenliste geleert');
    empty?.querySelector('a')?.focus();
  });

  load();

  return { load };
}
